import React, { useEffect, useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Container,
  Card,
  CardContent,
  Menu,
  MenuItem,
  Badge,
  Avatar,
  Chip,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Pets as PetsIcon,
  CalendarToday as CalendarIcon,
  Message as MessageIcon,
  Add as AddIcon,
  ExitToApp as LogoutIcon,
  AccountCircle,
  FavoriteOutlined,
  MedicalServices as VeterinaireIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { AddAnimalForm } from './AddAnimalForm';
import { MyAnimals } from './MyAnimals';
import { AnimalDetailProprietaire } from './AnimalDetailProprietaire';
import PrendreRendezVous from './PrendreRendezVous';
import MesRendezVous from './MesRendezVous';
import { MonVeterinaire } from './MonVeterinaire';
import { animalService } from '../../services/animalService';
import { veterinaireService } from '../../services/api';
import { Veterinaire } from '../../types';

export const ProprietaireDashboard: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedAnimalId, setSelectedAnimalId] = useState<number | null>(null);
  const { user, logout } = useAuth();
  const [animalCount, setAnimalCount] = useState<number>(0);
  const [animalsReloadKey, setAnimalsReloadKey] = useState<number>(0);
  const [veterinaireSelectionne, setVeterinaireSelectionne] = useState<Veterinaire | null>(null);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
  };

  // Charger le nombre d'animaux du propriétaire connecté
  useEffect(() => {
    let cancelled = false;
    const loadCount = async () => {
      try {
        const res = await animalService.getMyAnimals({ page_size: 1 });
        if (!cancelled) {
          setAnimalCount(res.count);
        }
      } catch (e) {
        if (!cancelled) setAnimalCount(0);
      }
    };
    loadCount();
    return () => { cancelled = true; };
  }, [animalsReloadKey]);

  // Charger le vétérinaire sélectionné
  useEffect(() => {
    let cancelled = false;
    const loadVeterinaire = async () => {
      if (user?.veterinaire_selectionne) {
        try {
          // D'abord récupérer tous les vétérinaires pour trouver celui qui correspond au User ID
          const allVeterinaires = await veterinaireService.getAll();
          const veterinaire = allVeterinaires.data.results.find(
            vet => vet.user === user.veterinaire_selectionne
          );
          
          if (veterinaire && !cancelled) {
            setVeterinaireSelectionne(veterinaire);
          } else if (!cancelled) {
            setVeterinaireSelectionne(null);
          }
        } catch (error) {
          console.error('Erreur lors du chargement du vétérinaire:', error);
          if (!cancelled) {
            setVeterinaireSelectionne(null);
          }
        }
      } else {
        if (!cancelled) {
          setVeterinaireSelectionne(null);
        }
      }
    };
    loadVeterinaire();
    return () => { cancelled = true; };
  }, [user]);

  const menuItems = [
    { id: 'dashboard', label: 'Tableau de bord', icon: <DashboardIcon /> },
    { id: 'animaux', label: 'Mes animaux', icon: <PetsIcon /> },
    { id: 'add-animal', label: 'Ajouter un animal', icon: <AddIcon /> },
    { id: 'mon-veterinaire', label: 'Mon vétérinaire', icon: <VeterinaireIcon /> },
    { id: 'rendez-vous', label: 'Mes rendez-vous', icon: <CalendarIcon /> },
    { id: 'prendre-rdv', label: 'Prendre RDV', icon: <CalendarIcon /> },
    { id: 'messages', label: 'Messages', icon: <Badge badgeContent={0} color="error"><MessageIcon /></Badge> },
  ];

  // Composant pour afficher la carte du vétérinaire
  const renderVeterinaireCard = () => {
    if (!veterinaireSelectionne) return null;

    return (
      <Card sx={{ mt: 4, mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <VeterinaireIcon color="primary" />
            <Typography variant="h6">
              Mon vétérinaire
            </Typography>
          </Box>
          
          <Box display="flex" alignItems="center" gap={3}>
            <Avatar sx={{ width: 60, height: 60, bgcolor: 'primary.main' }}>
              <PersonIcon sx={{ fontSize: 30 }} />
            </Avatar>
            
            <Box flex={1}>
              <Typography variant="h6" gutterBottom>
                Dr. {veterinaireSelectionne.prenom} {veterinaireSelectionne.nom}
              </Typography>
              
              {veterinaireSelectionne.specialite && (
                <Chip 
                  label={veterinaireSelectionne.specialite} 
                  color="primary" 
                  size="small" 
                  sx={{ mb: 1 }}
                />
              )}

              <Box display="flex" flexDirection="column" gap={0.5}>
                <Box display="flex" alignItems="center" gap={1}>
                  <EmailIcon color="action" fontSize="small" />
                  <Typography variant="body2">
                    {veterinaireSelectionne.email}
                  </Typography>
                </Box>
                
                <Box display="flex" alignItems="center" gap={1}>
                  <PhoneIcon color="action" fontSize="small" />
                  <Typography variant="body2">
                    {veterinaireSelectionne.telephone}
                  </Typography>
                </Box>

                {veterinaireSelectionne.clinique && (
                  <Box display="flex" alignItems="start" gap={1}>
                    <LocationIcon color="action" fontSize="small" sx={{ mt: 0.2 }} />
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {veterinaireSelectionne.clinique.nom}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {veterinaireSelectionne.clinique.ville}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            <Box>
              <Button
                variant="outlined"
                onClick={() => setCurrentView('mon-veterinaire')}
                sx={{ mr: 1 }}
              >
                Voir détails
              </Button>
              <Button
                variant="contained"
                onClick={() => setCurrentView('prendre-rdv')}
              >
                Prendre RDV
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderContent = () => {
    switch (currentView) {
      case 'dashboard':
        return (
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
              Espace propriétaire
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Bienvenue {user?.first_name} {user?.last_name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 3 }}>
              <Card sx={{ minWidth: 200, flex: 1 }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Mes animaux
                  </Typography>
                  <Typography variant="h4">
                    {animalCount}
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ minWidth: 200, flex: 1 }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    RDV à venir
                  </Typography>
                  <Typography variant="h4">
                    0
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ minWidth: 200, flex: 1 }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Messages non lus
                  </Typography>
                  <Typography variant="h4">
                    0
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            
            {/* Carte du vétérinaire sélectionné */}
            {renderVeterinaireCard()}
            
            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              Actions rapides
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Card sx={{ minWidth: 250, cursor: 'pointer' }} onClick={() => setCurrentView('add-animal')}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <AddIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6">
                    Ajouter un animal
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ minWidth: 250, cursor: 'pointer' }} onClick={() => setCurrentView('prendre-rdv')}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <CalendarIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6">
                    Prendre RDV
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ minWidth: 250, cursor: 'pointer' }} onClick={() => setCurrentView('messages')}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <MessageIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6">
                    Contacter vétérinaire
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Container>
        );
      case 'animaux':
        return (
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <MyAnimals 
              onAddAnimal={() => setCurrentView('add-animal')}
              onViewDetail={(animal) => {
                setSelectedAnimalId(animal.id!);
                setCurrentView('animal-detail');
              }}
            />
          </Container>
        );
      case 'animal-detail':
        return selectedAnimalId ? (
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <AnimalDetailProprietaire
              animalId={selectedAnimalId}
              onBack={() => {
                setSelectedAnimalId(null);
                setCurrentView('animaux');
              }}
            />
          </Container>
        ) : null;
      case 'add-animal':
        return (
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <AddAnimalForm 
              onSuccess={() => {
                setCurrentView('animaux');
                // Forcer le recalcul du nombre d'animaux
                setAnimalsReloadKey((k) => k + 1);
              }}
              onCancel={() => {
                setCurrentView('dashboard');
              }}
            />
          </Container>
        );
      case 'rendez-vous':
        return (
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <MesRendezVous 
              onPrendreRendezVous={() => setCurrentView('prendre-rdv')}
            />
          </Container>
        );
      case 'prendre-rdv':
        return (
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <PrendreRendezVous 
              onSuccess={() => setCurrentView('rendez-vous')}
              onCancel={() => setCurrentView('dashboard')}
            />
          </Container>
        );
      case 'mon-veterinaire':
        return (
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <MonVeterinaire 
              onSuccess={() => {
                // Rafraîchir éventuellement d'autres données
                console.log('Vétérinaire sélectionné avec succès');
              }}
            />
          </Container>
        );
      case 'messages':
        return (
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
              Messages avec votre vétérinaire
            </Typography>
            <Typography variant="body1">
              Interface de messagerie avec votre vétérinaire...
            </Typography>
          </Container>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* AppBar */}
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={() => setSidebarOpen(true)}
            edge="start"
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <FavoriteOutlined sx={{ mr: 2 }} />
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            VetCare - Mon espace
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 2 }}>
              {user?.first_name} {user?.last_name}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Drawer
        anchor="left"
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      >
        <Toolbar />
        <Box sx={{ width: 250 }}>
          <List>
            {menuItems.map((item) => (
              <ListItem
                key={item.id}
                onClick={() => {
                  setCurrentView(item.id);
                  if (item.id !== 'animal-detail') {
                    setSelectedAnimalId(null);
                  }
                  setSidebarOpen(false);
                }}
                sx={{ 
                  cursor: 'pointer',
                  backgroundColor: (currentView === item.id || (item.id === 'animaux' && currentView === 'animal-detail')) ? 'action.selected' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Profile Menu */}
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
      >
        <MenuItem onClick={handleLogout}>
          <LogoutIcon sx={{ mr: 1 }} />
          Déconnexion
        </MenuItem>
      </Menu>

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Toolbar />
        {renderContent()}
      </Box>
    </Box>
  );
}; 